import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator,
  faEuroSign,
  faClipboardCheck,
  faChartLine,
  faFileContract,
  faRocket,
  faCheck,
  faXmark,
  faLock,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useBranding } from 'modules/App/hooks/useBranding';
import UPGRADE_PREVIEWS from './upgradePreviews';
import { trackFreeSupplierUpgradeClicked } from 'helpers/mixpanel';
import i18n from 'i18n';

// Per-feature copy for every locked touchpoint in the free tier. Keeping the
// copy in one map means each upsell CTA opens the same modal with a pitch
// specific to the feature the user just tried to reach.
//
// title/headline/bullets are i18n GETTERS, not plain strings: UpsellCard.jsx
// imports this object directly (for `.icon`, at module scope — no hook/render
// context there), so it has to stay a plain object rather than become a
// buildX(t) factory. See CLAUDE.md i18n conventions — "getter pattern" for an
// export consumed by more than one file.
export const UPSELL_FEATURES = {
  sefa: {
    icon: faCalculator,
    get title() {
      return i18n.t('freeSupplier:upgradeModal.sefa.title', 'Emissions Calculator (SEFA)');
    },
    get headline() {
      return i18n.t(
        'freeSupplier:upgradeModal.sefa.headline',
        'Stop estimating — calculate your specific emission factors',
      );
    },
    get bullets() {
      return [
        i18n.t(
          'freeSupplier:upgradeModal.sefa.bullet1',
          'Calculate SEE values from your real activity data (fuels, electricity, heat)',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.sefa.bullet2',
          'Automatic attribution across production routes and precursors',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.sefa.bullet3',
          'Audit-ready calculation trail your customers can trust',
        ),
      ];
    },
  },
  costs: {
    icon: faEuroSign,
    get title() {
      return i18n.t('freeSupplier:upgradeModal.costs.title', 'CBAM Cost Estimator');
    },
    get headline() {
      return i18n.t(
        'freeSupplier:upgradeModal.costs.headline',
        'Know what CBAM will cost you — and your customers',
      );
    },
    get bullets() {
      return [
        i18n.t(
          'freeSupplier:upgradeModal.costs.bullet1',
          'Estimate certificate costs per good, per shipment, per customer',
        ),
        i18n.t('freeSupplier:upgradeModal.costs.bullet2', 'Live EU ETS certificate price tracking'),
        i18n.t(
          'freeSupplier:upgradeModal.costs.bullet3',
          'Compare your goods against EU benchmarks',
        ),
      ];
    },
  },
  verification: {
    icon: faClipboardCheck,
    get title() {
      return i18n.t('freeSupplier:upgradeModal.verification.title', 'Verification');
    },
    get headline() {
      return i18n.t(
        'freeSupplier:upgradeModal.verification.headline',
        'Get your emission factors verification-ready',
      );
    },
    get bullets() {
      return [
        i18n.t(
          'freeSupplier:upgradeModal.verification.bullet1',
          'Structured evidence collection for accredited verifiers',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.verification.bullet2',
          'Gap analysis against CBAM verification requirements',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.verification.bullet3',
          'Verified badge on your public portal — stand out to EU buyers',
        ),
      ];
    },
  },
  insights: {
    icon: faChartLine,
    get title() {
      return i18n.t('freeSupplier:upgradeModal.insights.title', 'Data Insights');
    },
    get headline() {
      return i18n.t('freeSupplier:upgradeModal.insights.headline', 'Turn your data into decisions');
    },
    get bullets() {
      return [
        i18n.t(
          'freeSupplier:upgradeModal.insights.bullet1',
          'Emissions intensity trends across periods and installations',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.insights.bullet2',
          'Benchmark your goods against sector averages',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.insights.bullet3',
          'Spot the production steps driving your carbon footprint',
        ),
      ];
    },
  },
  compliance: {
    icon: faFileContract,
    get title() {
      return i18n.t('freeSupplier:upgradeModal.compliance.title', 'Compliance & Monitoring Plan');
    },
    get headline() {
      return i18n.t(
        'freeSupplier:upgradeModal.compliance.headline',
        'Generate your monitoring plan from data you already have',
      );
    },
    get bullets() {
      return [
        i18n.t(
          'freeSupplier:upgradeModal.compliance.bullet1',
          'Auto-generate a CBAM monitoring plan from your template & diagram',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.compliance.bullet2',
          'Full communication template exports for every customer request',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.compliance.bullet3',
          'Stay ahead of the definitive-period reporting obligations',
        ),
      ];
    },
  },
  generic: {
    icon: faRocket,
    // Rendered brand-aware as `${brand.name} Premium` by UpgradeModal below —
    // this static title is never actually shown, kept only for shape parity
    // with the other feature entries.
    get title() {
      return i18n.t('freeSupplier:upgradeModal.generic.title', 'Premium');
    },
    get headline() {
      return i18n.t(
        'freeSupplier:upgradeModal.generic.headline',
        'Go beyond sharing — calculate, verify and comply',
      );
    },
    get bullets() {
      return [
        i18n.t(
          'freeSupplier:upgradeModal.generic.bullet1',
          'Calculate your SEE / SEFA from activity data',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.generic.bullet2',
          'Estimate CBAM costs and track certificate prices',
        ),
        i18n.t(
          'freeSupplier:upgradeModal.generic.bullet3',
          'Verification support and full compliance reporting',
        ),
      ];
    },
  },
};

// Module-scope (not exported/reused elsewhere) — built as a function of `t`,
// called via useMemo inside UpgradeModal below.
const buildComparisonRows = (t) => [
  {
    label: t(
      'freeSupplier:upgradeModal.comparison.centralizeShare',
      'Centralize & share your data',
    ),
    free: true,
  },
  {
    label: t(
      'freeSupplier:upgradeModal.comparison.sefaCalculation',
      'SEFA / emissions calculation',
    ),
    free: false,
  },
  {
    label: t('freeSupplier:upgradeModal.comparison.cbamCostEstimates', 'CBAM cost estimates'),
    free: false,
  },
  {
    label: t('freeSupplier:upgradeModal.comparison.verificationSupport', 'Verification support'),
    free: false,
  },
  {
    label: t('freeSupplier:upgradeModal.comparison.dataInsights', 'Data insights'),
    free: false,
  },
];

const UpgradeModalContext = createContext(null);

export function UpgradeModalProvider({ children }) {
  const [feature, setFeature] = useState(null);

  const openUpgrade = useCallback((key = 'generic') => {
    const featureKey = UPSELL_FEATURES[key] ? key : 'generic';
    trackFreeSupplierUpgradeClicked({ featureKey, source: 'upgrade_modal' });
    setFeature(featureKey);
  }, []);

  const value = useMemo(() => ({ openUpgrade }), [openUpgrade]);

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      <UpgradeModal
        featureKey={feature}
        onClose={() => setFeature(null)}
      />
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal() {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) throw new Error('useUpgradeModal must be used within UpgradeModalProvider');
  return ctx;
}

function UpgradeModal({ featureKey, onClose }) {
  const { t } = useTranslation('freeSupplier');
  const history = useHistory();
  const brand = useBranding();
  const isCarbonGlance = brand.key === 'carbonglance';
  const copy = UPSELL_FEATURES[featureKey] || UPSELL_FEATURES.generic;
  const comparisonRows = useMemo(() => buildComparisonRows(t), [t]);
  // The "generic" pitch is the only one that names the platform by brand —
  // per-feature titles (SEFA, cost estimator, ...) stay brand-neutral.
  const title =
    !featureKey || featureKey === 'generic'
      ? t('upgradeModal.brandPremiumTitle', '{{brandName}} Premium', { brandName: brand.name })
      : copy.title;

  return (
    <CgDialog
      open={!!featureKey}
      onOpenChange={(open) => !open && onClose()}
      width={640}
      title={title}
      description={t('upgradeModal.includedInPremium', 'Included in Premium')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <CgRadixButton
            variant="ghost"
            onClick={onClose}
          >
            {t('upgradeModal.maybeLater', 'Maybe later')}
          </CgRadixButton>
          {isCarbonGlance ? (
            <CgRadixButton
              variant="primary"
              icon={faRocket}
              onClick={() => {
                onClose();
                history.push('/supplier/upgrade');
              }}
            >
              {t('upgradeModal.viewPlans', 'View plans')}
            </CgRadixButton>
          ) : (
            // Fixpack item 17 (locked): whitelabel builds have no Stripe/CBAM
            // pricing to sell here — swap the plans CTA for a direct contact
            // link to the brand's own support address.
            <a
              href={`mailto:${brand.supportEmail}`}
              onClick={onClose}
              className="inline-flex items-center gap-1.5 text-sm font-medium rounded-xl bg-primaryColor text-white hover:bg-primaryColor/90 px-3 py-1.5 transition-colors no-underline"
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                className="w-3.5 h-3.5 shrink-0"
              />
              {t('upgradeModal.contactUs', 'Contact us')}
            </a>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Feature hero */}
        <div className="rounded-2xl bg-gradient-to-br from-primaryColor/15 via-primaryColor/5 to-slate-50 border border-primaryColor/20 p-5 flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-primaryColor/15 flex items-center justify-center shrink-0">
            <FontAwesomeIcon
              icon={copy.icon}
              className="text-primaryColor text-lg"
            />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800 m-0">{copy.headline}</p>
            <ul className="mt-2 mb-0 pl-0 list-none flex flex-col gap-1.5">
              {copy.bullets.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2 text-xs text-slate-600"
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="text-primaryColor mt-0.5 shrink-0"
                  />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Concrete preview of the locked feature */}
        {UPGRADE_PREVIEWS[featureKey] || UPGRADE_PREVIEWS.generic}

        {/* Free vs Premium mini table */}
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-[1fr_70px_84px] text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 px-4 py-2">
            <span />
            <span className="text-center">{t('upgradeModal.freeColumnHeader', 'Free')}</span>
            <span className="text-center text-primaryColor">
              {t('upgradeModal.premiumColumnHeader', 'Premium')}
            </span>
          </div>
          {comparisonRows.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-[1fr_70px_84px] items-center px-4 py-2 text-xs text-slate-600 border-t border-slate-100"
            >
              <span>{row.label}</span>
              <span className="text-center">
                {row.free ? (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="text-primaryColor"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="text-slate-300"
                  />
                )}
              </span>
              <span className="text-center">
                <FontAwesomeIcon
                  icon={faCheck}
                  className="text-primaryColor"
                />
              </span>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-slate-400 m-0 flex items-center gap-1.5">
          <FontAwesomeIcon icon={faLock} />
          {t(
            'upgradeModal.freePlanNeverExpires',
            'Your free plan never expires — upgrade only when you need calculations, verification or insights.',
          )}
        </p>
      </div>
    </CgDialog>
  );
}
